import type { TDecimal } from "../../utils/decimal.ts";
import type { ProjectStatus } from "./project.ts";

export const PROJECT_STATUSES = [
  "IN_PROGRESS",
  "WAITING_FOR_FIRST_RESPONSE",
  "IN_NEGOTIATION",
  "WON",
  "LOST",
  "CANCELLED",
] as const;

export const DEFAULT_PROJECT_STATUS: ProjectStatus = "IN_PROGRESS";

/** @deprecated former quote-only codes, kept so bookmarks and persisted filters predating the unification still resolve. */
const LEGACY_PROJECT_STATUSES: Readonly<Record<string, ProjectStatus>> = {
  ONGOING: "IN_PROGRESS",
  SENT: "WAITING_FOR_FIRST_RESPONSE",
  ACCEPTED: "WON",
  REFUSED: "LOST",
};

function isProjectStatus(value: string): value is ProjectStatus {
  return (PROJECT_STATUSES as readonly string[]).includes(value);
}

export function normalizeProjectStatuses(values: readonly string[]): ProjectStatus[] {
  const normalized = values.flatMap((value) => {
    if (isProjectStatus(value)) {
      return [value];
    }
    const legacy = LEGACY_PROJECT_STATUSES[value];
    return legacy ? [legacy] : [];
  });
  return [...new Set(normalized)];
}

export function isAwardedStatus(status: ProjectStatus): boolean {
  return status === "WON";
}

export function isSubmittedStatus(status: ProjectStatus): boolean {
  return status === "WAITING_FOR_FIRST_RESPONSE";
}

export type ProjectStatusOutcome = "won" | "lost" | "pending";

export const PROJECT_STATUS_OUTCOMES: Record<ProjectStatus, ProjectStatusOutcome> = {
  WON: "won",
  LOST: "lost",
  CANCELLED: "lost",
  IN_PROGRESS: "pending",
  IN_NEGOTIATION: "pending",
  WAITING_FOR_FIRST_RESPONSE: "pending",
};

export type StatusBucket = { readonly status: ProjectStatus } | { readonly statuses: readonly ProjectStatus[] };

export interface PriceableProject {
  status: ProjectStatus;
  computedPrice: TDecimal | null;
}

export function computeBucketStats(
  projects: readonly PriceableProject[],
  bucket: StatusBucket,
): { count: number; total: number } {
  const statuses = "statuses" in bucket ? bucket.statuses : [bucket.status];
  const statusSet = new Set<ProjectStatus>(statuses);
  return projects.reduce(
    (acc, p) => {
      if (!statusSet.has(p.status)) {
        return acc;
      }
      return {
        count: acc.count + 1,
        total: acc.total + (p.computedPrice?.toNumber() ?? 0),
      };
    },
    { count: 0, total: 0 },
  );
}
